import type { SiteApi } from "./siteApi";
import { DAY_MS, dayKey, type StatEvent } from "@/sites/tasma/lib/analytics";

// A website's own statistics events for a range of days, for the report worked out here in the browser (TASMA:
// lib/stats-report-core.ts; ReserVella: its Statistics screen, with the same report). Every website kind answers the
// same three addresses (Site Admin API v1): /stats/first, /stats/day/:day and /stats/recent. Finished days come as
// gzip files (GET /stats/day/:day: they never change, so they are kept in memory for the session and the browser may
// cache them); days not archived yet (today, yesterday until the nightly job) from /stats/recent, at most 3 days per
// request. Days before the website's first statistics aren't asked for. The event vocabulary is TASMA's (copied by
// every website).

const archived = new Map<string, StatEvent[]>();
const firsts = new Map<string, { at: number; day: Promise<string | null> }>();

async function gunzipLines(bytes: ArrayBuffer): Promise<StatEvent[]> {
  const text = await new Response(new Blob([bytes]).stream().pipeThrough(new DecompressionStream("gzip"))).text();
  const events: StatEvent[] = [];
  for (const line of text.split("\n")) {
    if (!line) continue;
    try {
      events.push(JSON.parse(line));
    } catch {
      // a damaged line must not hide the rest of the day
    }
  }
  return events;
}

const daysBetween = (from: string, to: string) => {
  const days: string[] = [];
  for (let t = Date.parse(`${from}T00:00:00Z`); t <= Date.parse(`${to}T00:00:00Z`); t += DAY_MS) days.push(dayKey(t));
  return days;
};

/** The website's first day with statistics (YYYY-MM-DD), or null before its first visit. Asked again after 5 minutes. */
export function firstDay(api: SiteApi): Promise<string | null> {
  const known = firsts.get(api.siteId);
  if (known && Date.now() - known.at < 5 * 60_000) return known.day;
  const day = api.get<{ day?: string | null }>("/stats/first").then((r) => r.day ?? null);
  firsts.set(api.siteId, { at: Date.now(), day });
  day.catch(() => firsts.delete(api.siteId));
  return day;
}

/** Every event between two days (inclusive, UTC, YYYY-MM-DD), oldest first. Six days at a time in parallel. */
export async function loadEvents(api: SiteApi, from: string, to: string): Promise<StatEvent[]> {
  const today = dayKey(Date.now());
  // If the first day can't be found out, every day is asked for, as before.
  const since = await firstDay(api).catch(() => undefined);
  if (since === null) return [];
  const days = daysBetween(from, to).filter((d) => d <= today && (!since || d >= since));
  const notArchived: string[] = [];
  const found: StatEvent[][] = [];
  for (let i = 0; i < days.length; i += 6) {
    await Promise.all(
      days.slice(i, i + 6).map(async (day) => {
        const key = `${api.siteId}|${day}`;
        if (archived.has(key)) return found.push(archived.get(key)!);
        // Today is never archived; anything else may be (the nightly job runs at 00:15 UTC).
        const bytes = day < today ? await api.bytes(`/stats/day/${day}`) : null;
        if (!bytes) return notArchived.push(day);
        const events = await gunzipLines(bytes);
        archived.set(key, events);
        found.push(events);
      }),
    );
  }
  // Runs of up to 3 consecutive days (the API takes a from–to range of at most 3 days).
  notArchived.sort();
  const runs: string[][] = [];
  for (const day of notArchived) {
    const run = runs.at(-1);
    if (run && run.length < 3 && Date.parse(`${day}T00:00:00Z`) - Date.parse(`${run.at(-1)}T00:00:00Z`) === DAY_MS) run.push(day);
    else runs.push([day]);
  }
  for (const chunk of runs) {
    const answer = await api.get<{ ok: boolean; events?: StatEvent[] }>(`/stats/recent?from=${chunk[0]}&to=${chunk[chunk.length - 1]}`);
    const wanted = new Set(chunk);
    found.push((answer.events ?? []).filter((e) => wanted.has(dayKey(e.t))));
  }
  return found.flat().sort((a, b) => a.t - b.t);
}
